import { Button, Flex, Icon, Icons, type TableColumnType } from "@ir/ui";
import {
  MasterDataDistributionCenter,
  type IMasterDataDistributionCenter,
  type MasterDataDistributionCenter as MasterDataDistributionCenterEntity,
} from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { MASTER_DATA_COLUMN_VERTICAL_ALIGN } from "./masterDataTable.shared";

export interface MasterDataDistributionCenterRow
  extends IMasterDataDistributionCenter {
  key: string;
}

/** The table identifies rows by `record.key`, which entities do not carry. */
export function mapDistributionCentersToRows(
  distributionCenters: MasterDataDistributionCenterEntity[],
): MasterDataDistributionCenterRow[] {
  return distributionCenters.map((entity) => ({
    ...MasterDataDistributionCenter.toJSON(entity),
    key: String(entity.distributionCenterId),
  }));
}

export function indexDistributionCentersById(
  distributionCenters: MasterDataDistributionCenterEntity[],
): Map<number, MasterDataDistributionCenterEntity> {
  const map = new Map<number, MasterDataDistributionCenterEntity>();
  for (const center of distributionCenters) {
    map.set(center.distributionCenterId, center);
  }
  return map;
}

export function buildDistributionCenterActionsColumn(
  onEdit: (entity: MasterDataDistributionCenterEntity) => void,
  centersById: Map<number, MasterDataDistributionCenterEntity>,
): TableColumnType<MasterDataDistributionCenterRow> {
  return {
    title: "Actions",
    dataIndex: "distributionCenterId",
    key: "actions",
    align: "center",
    fixed: "right",
    verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
    width: 80,
    cellRender: (value: unknown) => {
      const id = Number(value);
      const center = centersById.get(id);
      if (!center) return null;
      return (
        <HasAnyPermission requiredPermissions={[Permissions.MASTER_DATA_EDIT]}>
          <Flex align="center" justifyContent="center">
            <Button
              size="small"
              variant="outline"
              color="grey"
              aria-label={`Edit ${center.dcName}`}
              onPress={() => onEdit(center)}
            >
              <Icon name={Icons.Edit} size="small" />
            </Button>
          </Flex>
        </HasAnyPermission>
      );
    },
  };
}
